import { type ChangeEvent, type FormEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileUp, X } from "lucide-react";
import { ApiError } from "@/lib/api";
import { type ImportResult, useImportAudience } from "@/lib/audience-import";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Bring an audience list in from outside. Takes our own export back (csv /
 * xlsx / json / txt), somebody else's table, or a list pasted out of a chat —
 * the backend works out the shape, so there's no format picker here.
 */
export function ImportAudienceDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const importAudience = useImportAudience();
  const fileInput = useRef<HTMLInputElement>(null);

  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [resolveMissing, setResolveMissing] = useState(true);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setText("");
    setFile(null);
    setLabel("");
    setResolveMissing(true);
    setResult(null);
    setError(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  const pickFile = (e: ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
    setResult(null);
    setError(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    try {
      const r = await importAudience.mutateAsync({
        text: file ? undefined : text,
        file: file ?? undefined,
        label: label.trim() || null,
        resolve_missing: resolveMissing,
      });
      setResult(r);
      setText("");
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
    } catch (err) {
      setError(
        err instanceof ApiError ? err.detail : t("audience_import.error_generic"),
      );
    }
  };

  const lineCount = text.split("\n").filter((l) => l.trim()).length;
  const pending = result ? result.pending_usernames + result.pending_phones : 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent size="lg">
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader>
            <DialogTitle>{t("audience_import.title")}</DialogTitle>
            <DialogDescription className="whitespace-pre-line">
              {t("audience_import.hint")}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="audience_import_text">
                {t("audience_import.paste_label")}
                {!file && (
                  <span className="ml-2 font-normal text-fg-muted">
                    ({t("audience_import.lines_count", { count: lineCount })})
                  </span>
                )}
              </Label>
              <textarea
                id="audience_import_text"
                rows={9}
                value={text}
                disabled={!!file}
                onChange={(e) => setText(e.target.value)}
                placeholder={
                  "@anna_hr\n" +
                  "t.me/ivan_recruit\n" +
                  "512345678\n" +
                  "+79161234567\n" +
                  "5551234, ivan_p, Иван, Петров"
                }
                className="flex w-full rounded-lg border border-border bg-bg-secondary px-3 py-2 font-mono text-sm text-fg-primary placeholder:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInput}
                type="file"
                accept=".csv,.tsv,.txt,.json,.xlsx"
                onChange={pickFile}
                className="hidden"
                id="audience_import_file"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInput.current?.click()}
              >
                <FileUp className="mr-2 h-4 w-4" />
                {t("audience_import.pick_file")}
              </Button>
              {file && (
                <span className="inline-flex items-center gap-2 rounded-md bg-bg-secondary px-2 py-1 text-sm text-fg-secondary">
                  {file.name}
                  <button
                    type="button"
                    aria-label={t("audience_import.remove_file")}
                    onClick={() => {
                      setFile(null);
                      if (fileInput.current) fileInput.current.value = "";
                    }}
                    className="text-fg-muted hover:text-fg-primary"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              )}
              <span className="text-xs text-fg-muted">
                {t("audience_import.file_hint")}
              </span>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="audience_import_label">
                {t("audience_import.label_label")}
              </Label>
              <Input
                id="audience_import_label"
                value={label}
                maxLength={200}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t("audience_import.label_placeholder")}
              />
              <p className="text-xs text-fg-muted">
                {t("audience_import.label_hint")}
              </p>
            </div>

            <label className="flex cursor-pointer items-start gap-2">
              <Checkbox
                checked={resolveMissing}
                onCheckedChange={(v) => setResolveMissing(v === true)}
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="text-fg-primary">
                  {t("audience_import.resolve_label")}
                </span>
                <span className="block text-xs text-fg-muted">
                  {t("audience_import.resolve_hint")}
                </span>
              </span>
            </label>

            {result && (
              <Alert
                tone={result.created + result.updated + pending > 0 ? "success" : "warning"}
                title={t("audience_import.result_title", {
                  format: result.detected_format,
                })}
              >
                <ul className="space-y-1">
                  <li>
                    <span className="font-medium text-success">{result.created}</span>{" "}
                    {t("audience_import.res_created")}
                  </li>
                  {result.updated > 0 && (
                    <li>
                      <span className="font-medium text-fg-secondary">
                        {result.updated}
                      </span>{" "}
                      {t("audience_import.res_updated")}
                    </li>
                  )}
                  {pending > 0 && (
                    <li>
                      <span className="font-medium text-accent">{pending}</span>{" "}
                      {result.queued
                        ? t("audience_import.res_queued")
                        : t("audience_import.res_dropped")}
                    </li>
                  )}
                  {result.already_known > 0 && (
                    <li>
                      <span className="font-medium text-fg-muted">
                        {result.already_known}
                      </span>{" "}
                      {t("audience_import.res_known")}
                    </li>
                  )}
                  {result.duplicates_in_file > 0 && (
                    <li>
                      <span className="font-medium text-fg-muted">
                        {result.duplicates_in_file}
                      </span>{" "}
                      {t("audience_import.res_dupes")}
                    </li>
                  )}
                  {result.skipped_noise > 0 && (
                    <li>
                      <span className="font-medium text-fg-muted">
                        {result.skipped_noise}
                      </span>{" "}
                      {t("audience_import.res_noise")}
                    </li>
                  )}
                </ul>
                {result.queued && (
                  <p className="mt-2 text-xs text-fg-muted">
                    {t("audience_import.res_queued_hint")}
                  </p>
                )}
              </Alert>
            )}

            {error && <Alert tone="danger">{error}</Alert>}
          </DialogBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                {t("common.close")}
              </Button>
            </DialogClose>
            <Button
              type="submit"
              loading={importAudience.isPending}
              disabled={!file && !text.trim()}
            >
              {t("audience_import.run")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
